import { useState, useEffect, useCallback, useSyncExternalStore } from "react";

export type Theme = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

export interface ThemeBackgrounds {
  light: string;
  dark: string;
}

export interface UseThemeOptions {
  storageKey: string;
  backgrounds?: ThemeBackgrounds | undefined;
  /**
   * Pin the resolved theme, ignoring the stored preference and the OS
   * setting. For apps whose UI only has tokens for one theme.
   */
  forceTheme?: ResolvedTheme | undefined;
}

function readStored(storageKey: string): Theme {
  const stored = localStorage.getItem(storageKey);
  if (stored === "light" || stored === "dark" || stored === "system")
    return stored;
  return "system";
}

function getSystemTheme(): ResolvedTheme {
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function applyTheme(resolved: ResolvedTheme, backgrounds?: ThemeBackgrounds) {
  document.documentElement.classList.toggle("dark", resolved === "dark");
  document.documentElement.dataset["theme"] = resolved;
  if (backgrounds) {
    document.documentElement.style.background =
      resolved === "dark" ? backgrounds.dark : backgrounds.light;
  }
}

function subscribeToMediaQuery(callback: () => void) {
  const mq = window.matchMedia("(prefers-color-scheme: dark)");
  mq.addEventListener("change", callback);
  return () => mq.removeEventListener("change", callback);
}

let systemThemeSnapshot =
  typeof window !== "undefined" ? getSystemTheme() : "light";
function getSystemThemeSnapshot(): ResolvedTheme {
  return systemThemeSnapshot;
}

if (typeof window !== "undefined") {
  window
    .matchMedia("(prefers-color-scheme: dark)")
    .addEventListener("change", () => {
      systemThemeSnapshot = getSystemTheme();
    });
}

export function useTheme({
  storageKey,
  backgrounds,
  forceTheme,
}: UseThemeOptions) {
  const [theme, setThemeState] = useState<Theme>(() => readStored(storageKey));
  const systemPreference = useSyncExternalStore(
    subscribeToMediaQuery,
    getSystemThemeSnapshot,
  );

  const resolvedTheme: ResolvedTheme =
    forceTheme ?? (theme === "system" ? systemPreference : theme);

  useEffect(() => {
    applyTheme(resolvedTheme, backgrounds);
  }, [resolvedTheme, backgrounds]);

  const setTheme = useCallback(
    (newTheme: Theme) => {
      localStorage.setItem(storageKey, newTheme);
      setThemeState(newTheme);
    },
    [storageKey],
  );

  return { theme, setTheme, resolvedTheme } as const;
}

export function nextTheme(current: Theme): Theme {
  if (current === "light") return "dark";
  if (current === "dark") return "system";
  return "light";
}
